// import { Injectable } from '@angular/core';
// import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

// @Injectable({
//   providedIn: 'root',
// })
// export class AuthGuard implements CanActivate {
//   constructor(private router: Router) {}

//   canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
//     const accessToken = localStorage.getItem('accessToken');
//     const tokenExpiry = localStorage.getItem('tokenExpiry');

//     if (accessToken && tokenExpiry && Date.now() < +tokenExpiry) {
//       // Check if trying to access root or default workspace
//       if (state.url === '/' || state.url === '/workspace/projects') {
//         const userInfo = this.getUserInfo();

//         // Debugging: Log user info and visibility
//         console.log('UserInfo:', userInfo);
//         console.log('Visibility:', userInfo?.visibility);

//         if (userInfo?.visibility === 'vendor') {
//           console.log('Redirecting vendor to dashboard');
//           this.router.navigate(['/vendors/dashboard']);
//           return false;
//         }
//         // For non-vendors, allow access to default workspace
//         return true;
//       }
//       // For all other protected routes, allow access
//       return true;
//     } else {
//       this.clearAuthData();
//       localStorage.setItem('redirectUrl', state.url);
//       this.router.navigate(['/login']);
//       return false;
//     }
//   }

//   private getUserInfo(): any {
//     try {
//       const userInfoString = localStorage.getItem('userInfo');
//       return userInfoString ? JSON.parse(userInfoString) : null;
//     } catch (e) {
//       console.error('Error parsing userInfo', e);
//       return null;
//     }
//   }

//   private clearAuthData(): void {
//     localStorage.removeItem('accessToken');
//     localStorage.removeItem('refreshToken');
//     localStorage.removeItem('idToken');
//     localStorage.removeItem('tokenExpiry');
//     localStorage.removeItem('userInfo');
//   }
// }
import { Injectable } from '@angular/core';
import {
  CanActivate,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { canUseCommsHub, canUseInsights, isRestrictedEmailUser } from './restricted-email.guard';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(private router: Router) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean {
    const accessToken = localStorage.getItem('accessToken');
    const tokenExpiry = localStorage.getItem('tokenExpiry');

    if (accessToken && tokenExpiry && Date.now() < +tokenExpiry) {
      // Check if trying to access root or a default workspace landing page.
      // '/workspace/internal-team' is included because login now lands there
      // by default — the role routing below must still run so vendors,
      // customers and the content/prodcontroller team get sent to the right
      // console instead of being left on the internal-team page.
      // Use startsWith for internal-team because '/workspace/internal-team'
      // redirects to a default child ('/workspace/internal-team/dashboard'),
      // so state.url is the resolved child URL by the time this guard runs.
      if (
        state.url === '/' ||
        state.url === '/workspace/projects' ||
        state.url.startsWith('/workspace/internal-team')
      ) {
        const userInfo = this.getUserInfo();

        // Debugging: Log user info and visibility
        console.log('UserInfo:', userInfo);
        console.log('All userInfo keys:', Object.keys(userInfo || {}));

        // Check for visibility in different possible locations
        const isVendor = this.checkIfVendor(userInfo);
        console.log('Is vendor?', isVendor);

        if (isVendor) {
          console.log('Redirecting vendor to dashboard');
          this.router.navigate(['/workspace/vendor/dashboard']);
          return false;
        }

        // Restricted-menu users (e.g. prodcontroller@studioip.ai) land on
        // Vendor Reports regardless of their team. Checked before the team
        // check below so it still works when they aren't on the content team.
        //
        // ...but only when they have NOT asked for a specific page they are
        // allowed to open. This block fires for anything under
        // /workspace/internal-team, which now includes the Communication Hub.
        // Without the exception, a restricted user could reach the Hub by
        // clicking (a parent guard does not re-run on child navigation) and
        // then get bounced to Vendor Reports the moment they refreshed — the
        // page worked until you reloaded it, which is the worst kind of broken.
        //
        // CommsHubGuard on the route is what actually decides who may open it;
        // this only stops the landing redirect from overriding that decision.
        if (isRestrictedEmailUser()) {
          const wantsCommsHub =
            state.url.startsWith('/workspace/internal-team/comms');
          const wantsInsights =
            state.url.startsWith('/workspace/internal-team/insights');
          if (!((wantsCommsHub && canUseCommsHub())
                || (wantsInsights && canUseInsights()))) {
            this.router.navigate(['/workspace/ops-console/vendor-reports']);
            return false;
          }
          return true;
        }

        // Redirect content team to ops-console dashboard
        const team = (
          userInfo?.['custom:team'] ||
          userInfo?.team ||
          ''
        ).toLowerCase();
        if (team === 'content') {
          this.router.navigate(['/workspace/ops-console/dashboard']);
          return false;
        }

        // Redirect by visibility:
        //   • customer  → customer console
        //   • everyone else (internal team) → internal team console
        // (vendor is already handled above; content team is handled above.)
        const visibility = (
          userInfo?.['custom:visibility'] ||
          userInfo?.visibility ||
          ''
        ).toLowerCase();
        if (visibility === 'customer') {
          this.router.navigate(['/workspace/customer']);
          return false;
        }
        // Default internal-team user. If they're already anywhere under the
        // internal-team console, allow it through — navigating again would
        // loop the guard (the landing URL resolves to a child route).
        if (state.url.startsWith('/workspace/internal-team')) {
          return true;
        }
        this.router.navigate(['/workspace/internal-team']);
        return false;
      }
      // For all other protected routes, allow access
      return true;
    } else {
      // Determine the correct login page before clearing auth data
      const loginRoute = this.getLoginRouteForUser(state.url);
      this.clearAuthData();
      localStorage.setItem('redirectUrl', state.url);
      this.router.navigate([loginRoute]);
      return false;
    }
  }

  // The URL the user is trying to reach is the most reliable signal of which
  // login page they belong on — userInfo may be cleared (fresh deep-link from
  // an email, different browser, cleared cache) so it can't be the sole input.
  // Order of precedence:
  //   1. /workspace/vendor/*  → /vendors/login
  //   2. userInfo visibility  → matching login page
  //   3. fallback             → /login
  private getLoginRouteForUser(requestedUrl: string): string {
    if (requestedUrl && requestedUrl.startsWith('/workspace/vendor')) {
      return '/vendors/login';
    }
    const userInfo = this.getUserInfo();
    if (!userInfo) return '/login';
    const visibility = (
      userInfo['custom:visibility'] ||
      userInfo?.visibility ||
      ''
    ).toLowerCase();
    if (visibility === 'vendor') return '/vendors/login';
    if (visibility === 'customer') return '/customers/login';
    return '/login';
  }

  private checkIfVendor(userInfo: any): boolean {
    if (!userInfo) return false;

    // Try different ways to access the visibility property

    // 1. Direct property (standard)
    if (userInfo.visibility === 'vendor' || userInfo.visibility === 'VENDOR') {
      console.log('Found vendor in userInfo.visibility:', userInfo.visibility);
      return true;
    }

    // 2. Check for nested custom:visibility property
    if (
      userInfo['custom:visibility'] === 'vendor' ||
      userInfo['custom:visibility'] === 'VENDOR'
    ) {
      console.log(
        'Found vendor in userInfo["custom:visibility"]:',
        userInfo['custom:visibility'],
      );
      return true;
    }

    // 3. Check for custom object with visibility property
    if (
      userInfo.custom &&
      (userInfo.custom.visibility === 'vendor' ||
        userInfo.custom.visibility === 'VENDOR')
    ) {
      console.log(
        'Found vendor in userInfo.custom.visibility:',
        userInfo.custom.visibility,
      );
      return true;
    }

    // 4. Check all string properties for 'vendor' value
    for (const key in userInfo) {
      if (
        typeof userInfo[key] === 'string' &&
        (userInfo[key].toLowerCase() === 'vendor' || userInfo[key] === 'VENDOR')
      ) {
        console.log(`Found vendor in userInfo["${key}"]:`, userInfo[key]);
        return true;
      }
    }

    return false;
  }

  private getUserInfo(): any {
    try {
      const userInfoString = localStorage.getItem('userInfo');
      console.log('Raw userInfo from localStorage:', userInfoString);
      return userInfoString ? JSON.parse(userInfoString) : null;
    } catch (e) {
      console.error('Error parsing userInfo', e);
      return null;
    }
  }

  private clearAuthData(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('userInfo');
  }
}
